// review-remote-gh-evidence.ts
//
// The `gh` (github.com) leg of review-remote-adapter.ts's collectRemoteEvidence,
// moved out unchanged (200-line budget). The runner is injected — never a real
// `gh` call in tests.
import type {
  CollectRemoteEvidenceInput,
  GhRunner,
  RemoteEvidenceResult,
} from "./review-remote-adapter.js";

export async function collectViaGh(
  input: CollectRemoteEvidenceInput,
  ghRunner: GhRunner,
): Promise<RemoteEvidenceResult> {
  const res = await ghRunner(
    ["pr", "view", input.sourceBranch, "--json", "headRefOid,baseRefOid,statusCheckRollup"],
    input.repoDir,
  );
  if (res.status !== 0) {
    return {
      kind: "resource_missing",
      reason: `\`gh pr view\` thất bại (status ${res.status}): ${res.stderr.slice(0, 300)}`,
    };
  }
  let parsed: { headRefOid?: string; baseRefOid?: string; statusCheckRollup?: unknown[] };
  try {
    parsed = JSON.parse(res.stdout);
  } catch {
    return { kind: "resource_missing", reason: "gh pr view trả JSON không parse được." };
  }
  if (!parsed.headRefOid || !parsed.baseRefOid) {
    return { kind: "not_found", reason: "gh pr view thiếu headRefOid/baseRefOid." };
  }
  // No pipelineSha on this path: `gh` reports check runs, not a pipeline SHA,
  // so X2's binding refuses G3 here (and production wires no ghRunner yet).
  // CI gate: `statusCheckRollup` lists EVERY check (required or not — the
  // payload has no `isRequired`), so there is no single computed status to read
  // as GitLab's pipeline status. `ciStatus` stays "reported"/"unknown", which
  // release-ci-gate.ts refuses as not a pass; an aggregate belongs here only
  // together with a wired ghRunner and a pipelineSha equivalent.
  const rollup = parsed.statusCheckRollup ?? [];
  const ciStatus = rollup.length === 0 ? "unknown" : "reported";
  return {
    kind: "ok",
    evidence: {
      source: "gh",
      headSha: parsed.headRefOid,
      baseSha: parsed.baseRefOid,
      ciStatus,
      raw: parsed,
    },
  };
}
